/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {makeStyles} from '@material-ui/core';
import merge from 'lodash.merge';
import {GenericObject} from '@ion/core/src/types/entities';
import {DLSTheme} from '../types/types';

export type StylesDefinition = (theme: DLSTheme) => GenericObject;

export interface UseOverrideStylesProps {
  styleOverrides?: StylesDefinition;
  styleOverridesProps?: GenericObject;
}

/**
 * @useExtendStyles
 *
 * hook for re-using styles and providing overrides to the styles.
 * The base styles and overrides are functions that get the theme
 * passed as an argument.
 *
 * ```typescript
 *
 * import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
 *
 * const sharedStyles = (theme) => ({
 *   title : {
 *     color : theme.palette.red
 *   }
 * });
 *
 * // Optional
 * const overrides = (theme) => ({
 *   title : {
 *     color : theme.palette.pink
 *   }
 * });
 *
 * const Component = () => {
 *   const styles = useExtendStyles(sharedStyles, overrides);
 *   const classes = styles();
 *
 *   ...
 * }
 *
 * ```
 *
 */
export const useExtendStyles = (
  baseStyles: StylesDefinition,
  overrides?: StylesDefinition
): (props?: GenericObject) => GenericObject => makeStyles((theme: DLSTheme) => ({
  ...merge(baseStyles(theme), overrides?.(theme))
}));

export default useExtendStyles;
